import { Box, Button, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getAppServices } from "../../client/services/services";
import { useInternalContext } from "../../state/InternalContext";
import { useSiteContext } from "../../state/SiteContext";

const INVALID_LINK_MESSAGE = "This reset link is invalid or has expired.";
const MISMATCH_MESSAGE = "New password and confirm password do not match.";
const RULES_HELPER = "At least 8 characters, with an uppercase letter, a lowercase letter, a number, and a special character.";
const SET_FAILURE_MESSAGE = "We could not set the password. Wait a moment and try again.";

type PagePhase = "form" | "invalid" | "success";

export default function SetPassword() {
    const [searchParams] = useSearchParams();
    const queryToken = searchParams.get("token") ?? "";
    const [token] = useState(queryToken);
    const tokenMissing = token.trim().length === 0;
    const [phase, setPhase] = useState<PagePhase>(tokenMissing ? "invalid" : "form");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [formError, setFormError] = useState("");
    const [busy, setBusy] = useState(false);
    const { siteState } = useSiteContext();
    const { internalState } = useInternalContext();
    const services = getAppServices();

    const siteName = siteState.site?.name || "";
    const home = internalState.isCustomDomain ? "/" : (siteName ? `/${siteName}` : "/");

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (password !== confirmPassword) {
            setFormError(MISMATCH_MESSAGE);
            return;
        }
        setFormError("");
        setBusy(true);
        try {
            await services.auth.setPassword(token, password);
            setPhase("success");
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "";
            if (message === INVALID_LINK_MESSAGE) {
                setPhase("invalid");
                return;
            }
            if (message.startsWith("Password must")) {
                setFormError(message);
                return;
            }
            setFormError(SET_FAILURE_MESSAGE);
        } finally {
            setBusy(false);
        }
    };

    if (phase === "invalid") {
        return (
            <Box className="forgot-password-container">
                <Typography variant="h6" component="h2" className="forgot-password-title">
                    This link is invalid or has expired.
                </Typography>
                <Typography className="forgot-password-description">
                    Request a new link from the sign-in page.
                </Typography>
                <Button component="a" href={home} className="forgot-password-button-back">
                    Back to the site
                </Button>
            </Box>
        );
    }

    if (phase === "success") {
        return (
            <Box className="forgot-password-container">
                <Typography variant="h6" component="h2" className="forgot-password-success-title">
                    Your password is saved.
                </Typography>
                <Typography className="forgot-password-success-text">
                    You can sign in with it now.
                </Typography>
                <Button component="a" href={home} className="forgot-password-success-button">
                    Go to the site
                </Button>
            </Box>
        );
    }

    return (
        <Box component="form" className="forgot-password-form" onSubmit={handleSubmit}>
            <Typography variant="h6" component="h2" className="forgot-password-title">Set password</Typography>
            {formError ? <Typography color="error" className="forgot-password-error">{formError}</Typography> : null}
            <TextField
                margin="normal"
                required
                fullWidth
                id="new-password"
                label="New password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                helperText={RULES_HELPER}
                disabled={busy}
            />
            <TextField
                margin="normal"
                required
                fullWidth
                id="confirm-password"
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={busy}
            />
            <Button type="submit" fullWidth variant="contained" className="forgot-password-button" disabled={busy}>
                Set password
            </Button>
        </Box>
    );
}
